import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Plug, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [name, setName] = useState('');
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, bRes] = await Promise.all([api.get('/integrations'), api.get('/integrations/brands')]);
      setIntegrations(iRes.data);
      setBrands(bRes.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setSelectedBrand('');
    setName('');
    setConfig({});
    setTestResult(null);
    setPanelOpen(true);
  };

  const openEdit = (integ) => {
    setEditing(integ.id);
    setSelectedBrand(integ.brand);
    setName(integ.name);
    setConfig({});
    setTestResult(null);
    setPanelOpen(true);
  };

  const brandDef = brands.find(b => b.brand === selectedBrand);

  const save = async () => {
    if (!selectedBrand || !name) { toast.error('Brand e nome obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/integrations/${editing}`, { name, brand: selectedBrand, config });
        toast.success('Integrazione aggiornata');
      } else {
        await api.post('/integrations', { name, brand: selectedBrand, config });
        toast.success('Integrazione creata');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const testConnection = async (id) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/integrations/${id}/test`);
      setTestResult(res.data);
    } catch { setTestResult({ success: false, message: 'Errore test' }); }
    finally { setTesting(false); }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare questa integrazione?')) return;
    await api.delete(`/integrations/${id}`);
    load();
    toast.success('Integrazione eliminata');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Integrazioni Smart Home</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-integration-btn">
          <Plus size={14} /> Nuova Integrazione
        </button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {integrations.length === 0 ? (
            <div className="col-span-full card-dark rounded-lg p-12 text-center">
              <Plug size={32} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
              <p className="text-[var(--lux-text-muted-3)] text-sm">Nessuna integrazione. Aggiungine una.</p>
            </div>
          ) : integrations.map(integ => {
            const bDef = brands.find(b => b.brand === integ.brand);
            return (
              <div key={integ.id} className="card-dark rounded-lg p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-white font-medium">{integ.name}</h3>
                    <p className="text-[#c9a84c] text-xs mt-0.5">{bDef?.name || integ.brand}</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full mt-1.5 ${integ.is_active ? 'bg-green-500' : 'bg-[var(--lux-border-3)]'}`} />
                </div>
                {bDef?.description && <p className="text-[var(--lux-text-muted-3)] text-xs mb-4">{bDef.description}</p>}
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => testConnection(integ.id)} disabled={testing}
                    className="btn-outline-gold text-xs flex items-center gap-1 py-2 px-3" data-testid={`test-integration-${integ.id}`}>
                    {testing ? <Loader2 size={11} className="animate-spin" /> : <Plug size={11} />}
                    Test
                  </button>
                  <button onClick={() => openEdit(integ)} className="text-[var(--lux-text-muted-3)] hover:text-white p-2 transition-colors"><Edit size={14} /></button>
                  <button onClick={() => remove(integ.id)} className="text-[var(--lux-text-muted-3)] hover:text-red-400 p-2 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuova'} Integrazione</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <label className="label-muted block mb-1">Nome</label>
              <input className="input-dark" value={name} onChange={e => setName(e.target.value)} placeholder="Es: Nuki Cancello" data-testid="integration-name-input" />
            </div>
            <div>
              <label className="label-muted block mb-2">Sistema</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {brands.map(b => (
                  <button key={b.brand} onClick={() => { setSelectedBrand(b.brand); setConfig({}); }}
                    data-testid={`brand-${b.brand}`}
                    className={`p-3 text-left border rounded-sm transition-colors text-xs
                      ${selectedBrand === b.brand ? 'border-[#c9a84c] bg-[#c9a84c]/10 text-[#c9a84c]' : 'border-[#222] text-[var(--lux-text-muted-2)] hover:border-[var(--lux-border-4)]'}`}>
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
            {brandDef && (
              <div className="space-y-3">
                <p className="label-gold text-xs">Configurazione {brandDef.name}</p>
                {brandDef.fields.map(field => (
                  <div key={field.key}>
                    <label className="label-muted block mb-1">{field.label}{field.required ? ' *' : ''}</label>
                    {field.type === 'select' ? (
                      <select className="input-dark" value={config[field.key] || ''} onChange={e => setConfig(p => ({ ...p, [field.key]: e.target.value }))}>
                        <option value="">— Seleziona —</option>
                        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea className="input-dark h-20 resize-none" value={config[field.key] || ''}
                        onChange={e => setConfig(p => ({ ...p, [field.key]: e.target.value }))} />
                    ) : (
                      <input className="input-dark" type={field.type === 'password' ? 'password' : 'text'}
                        placeholder={field.type === 'url' ? 'https://' : ''}
                        value={config[field.key] || ''}
                        onChange={e => setConfig(p => ({ ...p, [field.key]: e.target.value }))}
                        data-testid={`integration-field-${field.key}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
            {testResult && (
              <div className={`flex items-start gap-3 p-3 rounded-lg border ${testResult.success ? 'border-green-800 bg-green-900/20' : 'border-red-800 bg-red-900/20'}`}>
                {testResult.success ? <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />}
                <p className="text-xs text-[var(--lux-text-sec)]">{testResult.message}</p>
              </div>
            )}
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2" data-testid="save-integration-btn">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
